import React from 'react';
import { useWikiV2Client } from '../../../hooks/useWikiV2';
import WikiIcon from '../shared/WikiIcon';
import { ASK_COPY } from './askCopy';
import { askStore, turnKind, useAskStore } from './askStore';
import { AskLines, AskNote, ReadRow } from './AskParts';
import { ClosestPageCard, SourceGrid } from './AskSources';
import { FeedbackPanel, VoteButtons } from './AskFeedback';
import AskTrace from './AskTrace';

/**
 * One thread row (D-ask §1.2 B): the user bubble, the pending row, or the
 * card for a turn — `kind` is the render switch (contracts/BE-ask.md):
 *   answer → answer card (§1.3)
 *   gap    → gap card (§1.4; `gap` and `unconfirmed`)
 *   notice → plain notice card (`ai_off` / `ai_unavailable` / `refused`; not
 *            in the prototype: the gap body with an info icon + sources)
 * plus an error card when the request itself failed.
 */

export function UserBubble({ text, live = true }) {
    // `data-wk-user` marks the live thread's questions for the auto-scroll.
    return (
        <div className="wk-ask-user" data-wk-user={live ? '1' : undefined} data-testid="wiki-ask-user">
            {text}
        </div>
    );
}

export function PendingRow() {
    return (
        <div className="wk-ask-pending" role="status" aria-live="polite" data-testid="wiki-ask-pending">
            <WikiIcon name="refresh-cw" size={14} className="wk-ask-pending-icon" />
            {ASK_COPY.pending}
        </div>
    );
}

function TraceToggle({ turnId, open }) {
    return (
        <button
            type="button"
            className="wk-ask-action wk-ask-action--trace"
            aria-expanded={open}
            onClick={() => askStore.toggleTrace(turnId)}
            data-testid="wiki-ask-trace-toggle"
        >
            <WikiIcon name="brain" size={14} />
            {open ? ASK_COPY.hideTrace : ASK_COPY.showTrace}
        </button>
    );
}

function OwnerButton({ turnId, name, busy }) {
    const client = useWikiV2Client();
    return (
        <button
            type="button"
            className="wk-ask-action wk-ask-action--owner"
            aria-disabled={busy || undefined}
            onClick={() => { if (!busy) askStore.handoff(client, turnId, name); }}
            data-testid="wiki-ask-owner"
        >
            <WikiIcon name="user" size={14} />
            {`Ask ${name}`}
        </button>
    );
}

/** The person to hand the conversation to, unless it's the asker. */
function handoffOwner(owner, label, meId) {
    if (!owner || !owner.user_id) return null;
    if (meId && owner.user_id === meId) return null;
    const name = label || owner.name;
    return name ? name : null;
}

function AnswerCard({ turn, meId }) {
    const turnId = turn.turn_id;
    const traceOpen = useAskStore((s) => Boolean(s.trace[turnId]));
    const fbOpen = useAskStore((s) => s.fbOpen === turnId);
    const busy = useAskStore((s) => s.busy[turnId] === 'handoff');
    const hasTrace = Boolean(turn.trace);
    const ownerName = handoffOwner(turn.owner, null, meId);

    return (
        <article className="wk-ask-card" data-kind="answer" data-testid="wiki-ask-answer" data-turn-id={turnId}>
            <div className="wk-ask-body">
                {turn.lines.length > 0
                    ? <AskLines lines={turn.lines} />
                    : (turn.text && <p className="wk-ask-p">{turn.text}</p>)}
                <AskNote note={turn.note} />
            </div>
            <SourceGrid sources={turn.sources} turnId={turnId} />
            <ReadRow read={turn.read} stance={turn.stance} />
            <div className="wk-ask-actions">
                <VoteButtons turn={turn} />
                <span className="wk-spacer" />
                {hasTrace && <TraceToggle turnId={turnId} open={traceOpen} />}
                {ownerName && <OwnerButton turnId={turnId} name={ownerName} busy={busy} />}
            </div>
            {fbOpen && <FeedbackPanel turnId={turnId} />}
            {hasTrace && traceOpen && (
                <AskTrace trace={turn.trace} brief={turn.brief} rewrite={turn.rewrite} />
            )}
        </article>
    );
}

function gapPillTitle(gap) {
    const parts = [];
    if (gap.notified) parts.push(`${gap.owner_label || 'The owner'} got a notification.`);
    if (Number(gap.count) > 1) parts.push(`Asked ${gap.count} times.`);
    return parts.join(' ') || undefined;
}

function GapCard({ turn, meId }) {
    const turnId = turn.turn_id;
    const traceOpen = useAskStore((s) => Boolean(s.trace[turnId]));
    const busy = useAskStore((s) => s.busy[turnId] === 'handoff');
    const gap = turn.gap || null;
    const hasTrace = Boolean(turn.trace);
    const ownerName = gap ? handoffOwner(gap.owner, gap.owner_label, meId) : handoffOwner(turn.owner, null, meId);
    const ownerLabel = gap?.owner_label || 'the category owner';
    const categoryName = gap?.category_name || 'Wiki';

    return (
        <article className="wk-ask-card" data-kind="gap" data-testid="wiki-ask-gap" data-turn-id={turnId}>
            <div className="wk-ask-gap-body">
                <span className="wk-ask-tile wk-ask-tile--gap" aria-hidden="true">
                    <WikiIcon name="search" size={15} />
                </span>
                <div className="wk-ask-gap-main">
                    <p className="wk-ask-gap-text">{turn.text}</p>
                    {gap && (
                        <span
                            className="wk-ask-gap-pill"
                            title={gapPillTitle(gap)}
                            data-notified={gap.notified ? 'true' : 'false'}
                            data-testid="wiki-ask-gap-pill"
                        >
                            <WikiIcon name="flag" size={12} />
                            {`Content gap sent to ${ownerLabel} (${categoryName})`}
                        </span>
                    )}
                </div>
            </div>
            <ClosestPageCard closest={gap?.closest} />
            <ReadRow read={turn.read} stance={turn.stance} />
            {(hasTrace || ownerName) && (
                <div className="wk-ask-actions">
                    <span className="wk-spacer" />
                    {hasTrace && <TraceToggle turnId={turnId} open={traceOpen} />}
                    {ownerName && <OwnerButton turnId={turnId} name={ownerName} busy={busy} />}
                </div>
            )}
            {hasTrace && traceOpen && (
                <AskTrace trace={turn.trace} brief={turn.brief} rewrite={null} showRewrite={false} />
            )}
        </article>
    );
}

function NoticeCard({ turn }) {
    const turnId = turn.turn_id;
    const traceOpen = useAskStore((s) => Boolean(s.trace[turnId]));
    const hasTrace = Boolean(turn.trace);
    return (
        <article
            className="wk-ask-card"
            data-kind="notice"
            data-status={turn.status || undefined}
            data-testid="wiki-ask-notice"
            data-turn-id={turnId}
        >
            <div className="wk-ask-gap-body">
                <span className="wk-ask-tile wk-ask-tile--notice" aria-hidden="true">
                    <WikiIcon name="info" size={15} />
                </span>
                <div className="wk-ask-gap-main">
                    <p className="wk-ask-gap-text">{turn.text}</p>
                </div>
            </div>
            <SourceGrid sources={turn.sources} turnId={turnId} />
            {hasTrace && (
                <div className="wk-ask-actions">
                    <span className="wk-spacer" />
                    <TraceToggle turnId={turnId} open={traceOpen} />
                </div>
            )}
            {hasTrace && traceOpen && (
                <AskTrace trace={turn.trace} brief={turn.brief} rewrite={null} showRewrite={false} />
            )}
        </article>
    );
}

function ErrorCard({ row }) {
    const client = useWikiV2Client();
    const sending = useAskStore((s) => s.sending);
    return (
        <article className="wk-ask-card" data-kind="error" data-testid="wiki-ask-error" role="alert">
            <div className="wk-ask-gap-body">
                <span className="wk-ask-tile wk-ask-tile--error" aria-hidden="true">
                    <WikiIcon name="alert-triangle" size={15} />
                </span>
                <div className="wk-ask-gap-main">
                    <p className="wk-ask-gap-text">{row.error}</p>
                    {/* Re-sends with the CURRENT scope + thread (a lost thread starts a new one). */}
                    <button
                        type="button"
                        className="wk-btn wk-btn--secondary wk-btn--sm wk-ask-retry"
                        disabled={sending}
                        onClick={() => askStore.retry(client, row.id)}
                        data-testid="wiki-ask-retry"
                    >
                        <WikiIcon name="refresh-cw" size={14} />
                        {ASK_COPY.tryAgain}
                    </button>
                </div>
            </div>
        </article>
    );
}

export default function AskTurn({ row, meId }) {
    if (row.role === 'user') return <UserBubble text={row.text} />;
    if (row.pending) return <PendingRow />;
    if (row.error) return <ErrorCard row={row} />;
    if (!row.turn) return null;
    const kind = turnKind(row.turn);
    if (kind === 'answer') return <AnswerCard turn={row.turn} meId={meId} />;
    if (kind === 'gap') return <GapCard turn={row.turn} meId={meId} />;
    return <NoticeCard turn={row.turn} />;
}
